import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Bell, Info, CheckCircle, AlertTriangle, AlertCircle, CheckCheck } from 'lucide-react';
import {
  announcementsService,
  Announcement,
  AnnouncementType,
  ANNOUNCEMENT_TYPE_SURFACE,
  ANNOUNCEMENT_TYPE_LABELS,
  isAnnouncementExpired,
  isFlyerAnnouncement,
  isVersionAnnouncement,
} from '../../services/announcementsService';
import { AnnouncementText } from '../../components/AnnouncementText';
import { Button } from '../../components/ui/Button';
import { formatDate } from '../../utils/helpers';

type Filtr = 'vse' | 'letaky' | 'verze' | 'ostatni';

const FILTRY: { key: Filtr; label: string }[] = [
  { key: 'vse', label: 'Všechny' },
  { key: 'letaky', label: 'Nové letáky' },
  { key: 'verze', label: 'Aplikace' },
  { key: 'ostatni', label: 'Ostatní' },
];

/** „Ostatní" je zbytek - co není o letáku ani o nové verzi. */
const sedneNaFiltr = (a: Announcement, filtr: Filtr): boolean => {
  switch (filtr) {
    case 'vse':
      return true;
    case 'letaky':
      return isFlyerAnnouncement(a);
    case 'verze':
      return isVersionAnnouncement(a);
    case 'ostatni':
      return !isFlyerAnnouncement(a) && !isVersionAnnouncement(a);
    default:
      return true;
  }
};

/** Parametr ?filtr= z odkazu (např. z verze v patičce) -> výchozí filtr. */
const filtrZUrl = (hodnota: string | null): Filtr =>
  FILTRY.some((f) => f.key === hodnota) ? (hodnota as Filtr) : 'vse';

const TYPE_ICON: Record<AnnouncementType, React.ComponentType<{ className?: string }>> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
};

/**
 * Přehled novinek pro přihlášeného uživatele – VČETNE už vypršelých, aby se
 * dalo ke staršímu sdělení vrátit i poté, co zmizelo z pruhu na přehledu.
 * Backend vrací jen novinky cílené na roli uživatele. Správa novinek
 * (zakládání, skrývání, mazání) je jinde – /announcements.
 */
export const NewsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Jen výchozí hodnota. Přepnutí záložky dál běží přes stav, adresa se nemění.
  const [filtr, setFiltr] = useState<Filtr>(() => filtrZUrl(searchParams.get('filtr')));

  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ['announcements', 'for-me'],
    queryFn: () => announcementsService.getMyAnnouncements(),
  });

  const readAllMutation = useMutation({
    mutationFn: () => announcementsService.markAllRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['announcements'] }),
  });

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const platnych = announcements.filter((a) => !isAnnouncementExpired(a)).length;
  const pocty = FILTRY.reduce(
    (m, f) => ({ ...m, [f.key]: announcements.filter((a) => sedneNaFiltr(a, f.key)).length }),
    {} as Record<Filtr, number>,
  );
  const zobrazene = announcements.filter((a) => sedneNaFiltr(a, filtr));

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate('/dashboard')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zpět na přehled
        </Button>
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-3xl font-bold text-gray-900">Novinky</h1>
          {announcements.some((a) => !a.isRead) && (
            <Button
              variant="outline"
              onClick={() => readAllMutation.mutate()}
              disabled={readAllMutation.isPending}
              className="flex-shrink-0"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Označit vše jako přečtené
            </Button>
          )}
        </div>
        <p className="mt-2 text-gray-600">
          Sdělení určená vám – včetně starších, kterým už skončila platnost.
          {announcements.length > 0 && ` Aktuálních: ${platnych} z ${announcements.length}.`}
        </p>
      </div>

      {announcements.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {FILTRY.map((f) => (
            <button
              key={f.key}
              onClick={() => setFiltr(f.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                filtr === f.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f.label} ({pocty[f.key]})
            </button>
          ))}
        </div>
      )}

      {announcements.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné novinky</h3>
          <p className="text-gray-600">Zatím tu nic není.</p>
        </div>
      ) : zobrazene.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nic k zobrazení</h3>
          <p className="text-gray-600">V tomhle filtru žádná novinka není.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {zobrazene.map((a) => {
            const Icon = TYPE_ICON[a.type];
            const expired = isAnnouncementExpired(a);
            return (
              <div
                key={a.id}
                className={`border-2 border-gray-300 rounded-lg p-5 ${
                  expired ? 'bg-gray-50 text-gray-500' : ANNOUNCEMENT_TYPE_SURFACE[a.type]
                }`}
              >
                <div className="flex items-start gap-3">
                  <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <h2 className="text-base font-semibold break-words min-w-0">
                        {!a.isRead && (
                          <span className="mr-2 align-middle px-2 py-0.5 rounded-full bg-blue-600 text-white text-xs font-semibold">
                            Nové
                          </span>
                        )}
                        {a.title}
                      </h2>
                      <span className="flex-shrink-0 text-xs opacity-75">{formatDate(a.createdAt)}</span>
                    </div>
                    <div className="mt-2">
                      <AnnouncementText text={a.content} radku={10} />
                    </div>
                    <p className="mt-3 text-xs opacity-75">
                      {ANNOUNCEMENT_TYPE_LABELS[a.type]}
                      {a.createdBy ? ` · ${a.createdBy.firstName} ${a.createdBy.lastName}` : ''}
                      {expired ? ' · už neplatí' : ''}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
